import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert/Alert';
import Stack from '@mui/material/Stack/Stack';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import DialogName from '@/shared/types/dialogName.enum';

import CashPaymentForm from './components/PaymentForm/PaymentForm';
import usePaymentForm from './components/PaymentForm/hooks/usePaymentForm';

type PaymentDialogProps = {
  variant: DialogName.ExternalTerminalPayment | DialogName.CashPayment;
  amountOwed: number;
  errors?: string[];
  onClose: () => void;
  onCreatePayment: (value: string) => void;
};

const PaymentDialog: FC<PaymentDialogProps> = ({
  variant,
  amountOwed,
  errors,
  onClose,
  onCreatePayment,
}) => {
  const { t } = useTranslation(['checkout', 'common']);

  const cashPaymentFormState = usePaymentForm({ amountOwed, variant });

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title:
          cashPaymentFormState.variant === DialogName.CashPayment
            ? t('checkout:cashPayment')
            : t('checkout:externalTerminal'),
        content:
          errors && errors.length ? (
            <Stack sx={{ py: 1 }}>
              {errors.map((error, index) => (
                <Alert key={index} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ) : (
            <CashPaymentForm paymentFormState={cashPaymentFormState} />
          ),
        actions: [
          {
            label: t('common:close'),
            onClick: onClose,
          },
          {
            disabled: parseFloat(cashPaymentFormState.amountReceived) <= 0,
            label:
              parseFloat(cashPaymentFormState.amountReceived) < amountOwed
                ? t('checkout:createPartialPayment')
                : t('checkout:createPayment'),
            variant: 'contained',
            onClick: () => {
              const paymentValue =
                parseFloat(cashPaymentFormState.amountReceived) -
                parseFloat(cashPaymentFormState.owedReceivedDifference);

              onCreatePayment(paymentValue.toFixed(2));
            },
          },
        ],
      }}
    />
  );
};

export default PaymentDialog;
